'use client'

import { useEffect } from 'react'
import { subscribeUploadActivity, isUploadInProgress } from './upload-activity'

/**
 * Asks the browser to confirm before a tab carrying an in-flight upload is
 * closed or navigated away from.
 *
 * Closing mid-upload discards work that has no other copy: the bytes are gone
 * and the batch was never submitted, so nothing appears in the transcript list
 * to tell the user what happened. The dialog now keeps the upload visible on
 * screen, which makes an accidental close both more survivable to warn about and
 * more jarring to lose.
 *
 * The browser shows its own generic wording — `preventDefault()` is all a page
 * gets to say, and the custom message every browser dropped years ago is not
 * worth pretending otherwise.
 */
export function useUploadUnloadGuard(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return

    let armed = false
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Legacy browsers key off a non-empty returnValue rather than the
      // prevented default. Harmless where it is ignored.
      event.returnValue = ''
    }

    const sync = () => {
      const shouldGuard = isUploadInProgress()
      if (shouldGuard === armed) return
      armed = shouldGuard
      if (shouldGuard) window.addEventListener('beforeunload', onBeforeUnload)
      else window.removeEventListener('beforeunload', onBeforeUnload)
    }

    // The listener is attached only while an upload is running: an unconditional
    // one turns every ordinary tab close into a confirmation prompt, and users
    // learn to dismiss it without reading — which is worse than no guard at all.
    sync()
    const unsubscribe = subscribeUploadActivity(sync)
    return () => {
      unsubscribe()
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [])
}
